// Selection cube intent handlers (move/position/size/visibility).

import { INTENTS } from '@store/intents';
import { params } from '@store/instance';
import {
    setSelectionSize as picksSetSelectionSize,
    setSelectionVisibility as picksSetSelectionVisibility,
} from '@scene/picking';
import {
    updateSelectedRegion,
    updateSelectionVisuals,
} from '@scene/selection';
import { buildRegionContext } from '@scene/regionContext';
import { physicalToThreeJS } from '@adapters/coordinates';
import type { Store } from '@store/store';
import type { State } from '@store/schema';
import type { ViewerContext } from '../context';

export function register(store: Store<State>, viewer: ViewerContext): Array<() => void> {
    const offs: Array<() => void> = [];

    offs.push(store.onIntent(INTENTS.SET_SELECTION_SIZE, (p) => {
        if (!p || typeof p.value !== 'number') return;
        params.selectionCubeSize = p.value;
        viewer.objects.selection.size = p.value;
        picksSetSelectionSize(p.value);
    }));

    offs.push(store.onIntent(INTENTS.SET_SELECTION_VISIBILITY, (p) => {
        if (!p) return;
        const visible = !!p.visible;
        params.showSelectionCube = visible;
        picksSetSelectionVisibility(visible);
    }));

    // Center-dot + projection-line overlay (the Layers "Center + projections"
    // row). Distinct from the cube toggle above.
    offs.push(store.onIntent(INTENTS.SET_SELECTION_OVERLAY_VISIBILITY, (p) => {
        if (!p) return;
        const visible = !!p.visible;
        params.showSelection = visible;
        const sel = viewer.objects.selection;
        if (sel.centerDot) sel.centerDot.visible = visible;
        if (sel.projectionLines) sel.projectionLines.visible = visible;
    }));

    // setSelectionPosition: write params.position without touching mesh (used by
    // syncs after dataset switch). To MOVE the cube, use 'moveSelection' instead.
    offs.push(store.onIntent(INTENTS.SET_SELECTION_POSITION, (p) => {
        if (!p) return;
        store.set('params.position', {
            x: Math.round((+p.x || 0) * 100) / 100,
            y: Math.round((+p.y || 0) * 100) / 100,
            z: Math.round((+p.z || 0) * 100) / 100,
        });
        store.dispatch(INTENTS.UI_PANE_REFRESH);
    }));

    offs.push(store.onIntent(INTENTS.MOVE_SELECTION, (p) => {
        const inv = viewer.objects.selection.invisibleMesh;
        if (!inv || !p) return;
        const grid = viewer.grid;
        const cx = Math.min(grid.xMax, Math.max(grid.xMin, +p.x || 0));
        const cy = Math.min(grid.yMax, Math.max(grid.yMin, +p.y || 0));
        const cz = Math.min(grid.zMax, Math.max(grid.zMin, +p.z || 0));
        inv.position.copy(physicalToThreeJS(cx, cy, cz));
        updateSelectionVisuals(viewer, params);
        updateSelectedRegion(buildRegionContext(viewer, params), inv.position);
        store.set('params.position', {
            x: Math.round(cx * 100) / 100,
            y: Math.round(cy * 100) / 100,
            z: Math.round(cz * 100) / 100,
        });
        store.dispatch(INTENTS.UI_PANE_REFRESH);
    }));

    return offs;
}
